import {useStaticQuery, graphql} from 'gatsby'
import {LazyMotion} from 'motion/react'
import React from 'react'
import type {ReactNode} from 'react'
import {StyleSheetManager} from 'styled-components'

import Footer from '@/components/footer'
import HeaderContainer from '@/components/header'
import {NotificationBanner} from '@/components/notification-banner'
import SkipToContent from '@/components/skip-to-content'
import {TypeformSurvey} from '@/components/typeform-survey'
import type {WrapPageElementQuery} from '@/graphql-types'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import {useSWPostMessage} from '@/hooks/use-sw-post-message'
import {GlobalStringsProvider} from '@/providers/global-strings'
import {LocaleProvider} from '@/providers/locale'
import {PageInfoProvider} from '@/providers/page-info'
import {SignupModalProvider} from '@/providers/signup-modal'
import GlobalStyles from '@/styles/global-styles'
import {legacyLayerWrapPlugin} from '@/styles/legacy-wrap-stylis-plugin'
import type {PageProps} from '@/types/page-props'
import {getNotificationBannerContent} from '@/utils/get-notification-banner-content'

type Props = Omit<PageProps, 'children'> & {children: ReactNode}

const motionFeatures = () => import('@/utils/motion-features').then((res) => res.default)

export const WrapPageElement = ({children, ...props}: Props) => {
    const pathnameIncludes = (path: string) => new RegExp(path).test(props.location.pathname)

    const {headerConfig, footerConfig, locale} = props.pageContext

    // Any doc type whose page query selects `notification_banner { ...NotificationBannerLink }`
    // surfaces it here — the helper scans props.data generically, so adding the field to a new
    // doc type needs no change in this file (the container renders nothing when content is null).
    const notificationBannerContent = getNotificationBannerContent(props.data)

    // We share the page locale code to the SW thread in order to optimize the Previews api calls.
    // Note that in production this hook is replaced with a dummy function since Previews are disabled.
    useSWPostMessage({type: 'SET_PREVIEWS_LANGUAGE', message: locale})

    const hasFinancialPartnerDisclaimer = locale === 'en-gb'

    const {allPrismicGlobalStrings, allPrismicTypeformSurveys} =
        useStaticQuery<WrapPageElementQuery>(query)
    const [globalStringsContent] = useLocalizedContent(allPrismicGlobalStrings.nodes, {
        localeCode: locale,
    })
    const [typeformSurveysContent] = useLocalizedContent(allPrismicTypeformSurveys.nodes, {
        localeCode: locale,
        fallbackToDefaultLocale: false,
    })

    const typeformHiddenPaths =
        typeformSurveysContent?.data?.hidden_survey_paths?.map(({path}) => path) ?? []
    const showTypeformSurvey =
        !!typeformSurveysContent && !typeformHiddenPaths.some(pathnameIncludes)

    const globalStrings = {
        ...globalStringsContent.data,
        recaptcha_and_privacy_terms: globalStringsContent.data.recaptcha_and_privacy_terms.richText,
        accept_terms_and_privacy: globalStringsContent.data.accept_terms_and_privacy.richText,
        clearbitCredits: globalStringsContent.data.clearbit_credits.richText,
        marketingEmailsConsentText:
            globalStringsContent.data.marketing_emails_consent_text.richText,
        yourEmailLabel: globalStringsContent.data.your_email_label,
        playVideo: globalStringsContent.data.play_video,
        localeSelectorGlobalLabel: globalStringsContent.data.locale_selector_global_label,
        signUp: globalStringsContent.data.sign_up,
        skipToContent: globalStringsContent.data.skip_to_content,
        consent_label: globalStringsContent.data.consent_label.richText,
        watchTheVideo: globalStringsContent.data.watch_the_video,
        privacyConsentText: globalStringsContent.data.privacy_consent_text.richText,
        financial_partner_disclaimer: hasFinancialPartnerDisclaimer
            ? globalStringsContent.data.financial_partner_disclaimer
            : '',
    }

    return (
        <StyleSheetManager stylisPlugins={[legacyLayerWrapPlugin]}>
            <GlobalStyles />

            <PageInfoProvider {...props}>
                <LocaleProvider localeCode={locale}>
                    <GlobalStringsProvider content={globalStrings}>
                        <LazyMotion features={motionFeatures} strict>
                            <SkipToContent />

                            {showTypeformSurvey && (
                                <TypeformSurvey
                                    formId={typeformSurveysContent.data.typeform_id}
                                    isStagingOnly={typeformSurveysContent.data.is_staging_only}
                                />
                            )}

                            <SignupModalProvider>
                                <NotificationBanner content={notificationBannerContent} />
                                <HeaderContainer
                                    hideHeader={headerConfig?.isHidden}
                                    logoOnly={headerConfig?.logoOnly}
                                    ctaDestination={headerConfig?.ctaDestination}
                                    exploreProductDestination={
                                        headerConfig?.exploreProductDestination
                                    }
                                />
                                {children}
                                {!footerConfig?.isHidden && (
                                    <Footer hideLinks={footerConfig?.hideLinks} />
                                )}
                            </SignupModalProvider>
                        </LazyMotion>
                    </GlobalStringsProvider>
                </LocaleProvider>
            </PageInfoProvider>
        </StyleSheetManager>
    )
}

const query = graphql`
    query WrapPageElement {
        allPrismicTypeformSurveys {
            nodes {
                lang
                data {
                    typeform_id
                    is_staging_only
                    hidden_survey_paths {
                        path
                    }
                }
            }
        }
        allPrismicGlobalStrings {
            nodes {
                lang
                data {
                    close_button_text
                    visit_website
                    read_more
                    learn_more
                    show_more
                    watch_now
                    recaptcha_and_privacy_terms {
                        richText
                    }
                    show_me
                    all
                    copied_to_clipboard
                    something_went_wrong
                    first_name
                    last_name
                    work_email
                    company_name
                    company_size
                    select
                    phone_number
                    submit
                    accept_terms_and_privacy {
                        richText
                    }
                    this_field_is_required
                    must_be_company_email
                    sole_trader
                    employee
                    employees
                    your_full_name
                    your_work_email
                    please_select_number_of_employees
                    please_enter_your_work_email
                    please_enter_a_work_email
                    sticky_cta_button_text
                    play
                    admin
                    industry
                    location
                    days
                    hours
                    minutes
                    back
                    continue
                    home
                    menu
                    invalid_email_error
                    loading
                    clearbit_credits {
                        richText
                    }
                    play_video
                    preview
                    mins_read
                    min_read
                    latest_articles
                    tags
                    panel
                    related_posts_text
                    sequence_posts_text
                    non_work_email_error_cta_text
                    non_work_email_error
                    country
                    locale_selector_global_label
                    marketing_emails_consent_text {
                        richText
                    }
                    your_email_label
                    sign_up
                    skip_to_content
                    consent_label {
                        richText
                    }
                    country_placeholder
                    discovered_via_label
                    discovered_via_placeholder
                    demo_submit_text
                    other
                    peer_recommendation
                    trust_pilot
                    google_search
                    youtube
                    tv
                    outdoor_advertising
                    social_media
                    press
                    first_name_error
                    last_name_error
                    phone_number_error
                    company_size_error
                    country_error
                    has_consented_error
                    get_started
                    watch_the_video
                    file_upload_too_large
                    file_type
                    upload_text
                    your_message
                    send_message
                    form_submission_error
                    num_customers
                    consents_error
                    feedback
                    interest_message_label
                    contact_sales
                    back_to_pleo_io
                    financial_partner_disclaimer
                    privacy_consent_text {
                        richText
                    }
                    email
                    address
                    city
                    postcode
                    state
                    invoice_number
                    issued_on
                    due_date
                    company_logo
                    currency
                    free_plan_cost_label
                    panels
                    copy_to_clipboard
                    filter_items
                    previous
                    next
                    slides
                    labels
                }
            }
        }
    }
`
